import { type FC } from "react";

import { getRelationshipLabel } from "@/shared/constants/relationshipOptions";
import { contactFullName } from "@/shared/utils/contactUtils";

import type { PersonContact } from "@/shared/types/estateContact";

import { SummaryCard, SummaryRow } from "./SummaryCard";
import { YES_NO_LABELS, findContact, formatLongDate } from "../summaryFormat";

import type { EstateCase, EstateTrust, EstateWill } from "../hooks/useEstateSummaryRecords";

const COPY_TYPE_LABELS: Record<string, string> = {
  original: "Original",
  copy: "Copy",
  not_sure: "Not sure",
};

const HELD_BY_LABELS: Record<string, string> = {
  court: "filed with the court",
  attorney: "held by the attorney",
  family: "held by family",
  safe_deposit_box: "in a safe deposit box",
};

const TRUST_TYPE_LABELS: Record<string, string> = {
  revocable: "Revocable living trust",
  irrevocable: "Irrevocable trust",
  not_sure: "Not sure",
};

const CASE_TYPE_LABELS: Record<string, string> = {
  probate: "Probate",
  administration: "Administration",
  small_estate: "Small estate",
  ancillary: "Ancillary",
};

const CASE_STATUS_LABELS: Record<string, string> = {
  not_filed: "Not filed",
  filed: "Filed",
  closed: "Closed",
};

interface WillTrustSectionProps {
  will: EstateWill | null;
  hasTrust: string | null;
  trust: EstateTrust | undefined;
  contacts: PersonContact[] | undefined;
}

export const WillTrustSection: FC<WillTrustSectionProps> = ({
  will,
  hasTrust,
  trust,
  contacts,
}) => {
  const name = (id: number | null | undefined) => contactFullName(findContact(contacts, id));
  const copy = will?.copy_type ? COPY_TYPE_LABELS[will.copy_type] : "";
  const heldBy = will?.held_by ? HELD_BY_LABELS[will.held_by] : "";

  return (
    <SummaryCard title="Will and trust">
      <SummaryRow label="Will">{will?.has_will && YES_NO_LABELS[will.has_will]}</SummaryRow>
      {will?.has_will === "yes" && (
        <>
          <SummaryRow label="Will dated">{formatLongDate(will?.date_signed)}</SummaryRow>
          <SummaryRow label="Codicils" />
          <SummaryRow label="Original or copy">
            {[copy, heldBy].filter(Boolean).join(", ")}
          </SummaryRow>
          <SummaryRow label="Named executor">{name(will?.executor_contact_id)}</SummaryRow>
          <SummaryRow label="Successor executor">
            {name(will?.successor_executor_contact_id)}
          </SummaryRow>
        </>
      )}
      <SummaryRow label="Trust">{hasTrust && YES_NO_LABELS[hasTrust]}</SummaryRow>
      {hasTrust === "yes" && (
        <>
          <SummaryRow label="Trust type">
            {trust?.trust_type && TRUST_TYPE_LABELS[trust.trust_type]}
          </SummaryRow>
          <SummaryRow label="Acting trustee">{name(trust?.acting_trustee_contact_id)}</SummaryRow>
          <SummaryRow label="Successor trustee">
            {name(trust?.successor_trustee_contact_id)}
          </SummaryRow>
          <SummaryRow label="Pour-over will">
            {trust?.has_pour_over_will && YES_NO_LABELS[trust.has_pour_over_will]}
          </SummaryRow>
        </>
      )}
    </SummaryCard>
  );
};

interface CourtAuthoritySectionProps {
  estateCase: EstateCase | null;
  contacts: PersonContact[] | undefined;
}

export const CourtAuthoritySection: FC<CourtAuthoritySectionProps> = ({ estateCase, contacts }) => {
  const fiduciary = findContact(contacts, estateCase?.fiduciary_contact_id);
  const attorney = findContact(contacts, estateCase?.attorney_contact_id);
  const authority =
    estateCase?.status === "letters_granted"
      ? ["Granted", formatLongDate(estateCase.issuance_date)].filter(Boolean).join(" ")
      : estateCase && CASE_STATUS_LABELS[estateCase.status];
  const limitations = estateCase?.limitations || (estateCase?.letter_type === "full" ? "None" : "");

  return (
    <SummaryCard title="Court and authority">
      <SummaryRow label="Authority">{authority}</SummaryRow>
      <SummaryRow label="Proceeding">
        {estateCase && CASE_TYPE_LABELS[estateCase.case_type]}
      </SummaryRow>
      <SummaryRow label="Docket number">{estateCase?.case_number}</SummaryRow>
      <SummaryRow label="Fiduciary">
        {fiduciary &&
          [
            contactFullName(fiduciary),
            fiduciary.relationship && getRelationshipLabel(fiduciary.relationship),
          ]
            .filter(Boolean)
            .join(", ")}
      </SummaryRow>
      <SummaryRow label="Filed">{formatLongDate(estateCase?.filing_date)}</SummaryRow>
      <SummaryRow label="Authority limitations">{limitations}</SummaryRow>
      <SummaryRow label="Attorney on the estate">
        {attorney &&
          [contactFullName(attorney), attorney.company_or_firm].filter(Boolean).join(", ")}
      </SummaryRow>
    </SummaryCard>
  );
};
